import { useEffect, useRef } from 'react';
import Trans from 'next-translate/Trans';

// Third-party
import PropTypes from 'prop-types';
import clsx from 'clsx';

// Utils
import { CAPTION, TAG } from '@/lib/manage/mentions';
import getIGUsernameTags, { getIGUsernameLink } from '@/utils/text/getIGUsernameTags';

// Hooks
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';
import useAwsTranslate from '@/utils/hooks/language/useAwsTranslate';
import useUser from '@/utils/hooks/swr/useUser';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import { DefaultAvatar } from '@/components/base/Avatar';
import Comment from '@/components/base/Comment';
import AwsTranslateSameLanguageBanner from '@/components/composite/Banners/AwsTranslateSameLanguageBanner';

const Mention = ({ className, markAsDisplayed, onReply, mention }) => {
    const mentionRef = useRef();
    const mentionInViewPort = useIntersectionObserver(mentionRef, '0px');

    useEffect(() => {
        if (!mention?.is_read && mentionInViewPort) {
            markAsDisplayed(mention.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mentionInViewPort]);

    return (
        <Comment.Container
            className={clsx('relative', className)}
            commentRef={mentionRef}
            isRead={mention?.is_read}>
            <div className="relative w-full space-y-3">
                <Mention.Header
                    comment={{
                        ...mention,
                        data: { username: mention?.media?.username || mention?.comment?.username }
                    }}
                    onReply={onReply}
                />
                <Mention.Message mention={mention} onReply={onReply} />

                <Mention.Thread mention={mention} onReply={onReply} />
            </div>
        </Comment.Container>
    );
};

Mention.propType = {
    className: PropTypes.string,
    emojiPortalContainer: PropTypes.element,
    markAsDisplayed: PropTypes.func,
    mention: PropTypes.shape({
        comment: PropTypes.object,
        comment_id: PropTypes.string,
        id: PropTypes.string,
        is_read: PropTypes.bool,
        media: PropTypes.object,
        media_id: PropTypes.string,
        mentioned_in: PropTypes.string,
        social_profile_id: PropTypes.string
    }).isRequired,
    onReply: PropTypes.func.isRequired
};

/**
 * Header
 */

const Header = ({ comment, onReply }) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const message = comment?.data?.parent?.message ?? (comment?.data?.message || '');
    const { formatTextWithComponent } = useTransformStringOnOffset();
    const tags = getIGUsernameTags(message);
    const { awsTranslation, awsToggleTranslation } = useAwsTranslate(
        formatTextWithComponent(message, tags)
    );
    const { isOpen: isOpenAwsTSLBanner, toggle: toggleAwsTSLBanner } = useToggle();

    return (
        <div className="relative mb-1 flex w-full items-center gap-2">
            <Comment.Header socialProfile={activeSocialProfile} comment={comment} />

            <div className="flex flex-1 items-center justify-between gap-4">
                <Comment.Date
                    created={comment?.created}
                    user={user}
                    className="text-xxs uppercase tracking-wider text-icoGray-500"
                    showFullDate
                />

                <div className="flex items-center gap-3">
                    {comment?.mentioned_in !== TAG && (
                        <Comment.ReplyButton
                            handleActiveFormReply={() => onReply({ currentComment: comment })}
                            isIcon
                        />
                    )}

                    <Comment.TranslateButton
                        awsTranslation={awsTranslation}
                        awsToggleTranslation={awsToggleTranslation}
                        isOpenAwsTSLBanner={isOpenAwsTSLBanner}
                        toggleAwsTSLBanner={toggleAwsTSLBanner}
                        isIcon
                    />
                </div>
            </div>

            {isOpenAwsTSLBanner && (
                <AwsTranslateSameLanguageBanner
                    className="absolute right-0 top-0 z-1 mt-4"
                    onClose={toggleAwsTSLBanner}
                />
            )}
        </div>
    );
};

Mention.Header = Header;

const SubHeaderBasic = ({ username }) => {
    return (
        <div className="mb-1 flex items-center">
            <DefaultAvatar fallback={username?.charAt(0)?.toUpperCase() || ''} size="xs" />
            <a
                href={getIGUsernameLink(username)}
                className="ml-2 text-sm hover:text-icoBlue"
                target="_blank"
                rel="noopener noreferrer">
                {username}
            </a>
        </div>
    );
};

Mention.SubHeaderBasic = SubHeaderBasic;

const Message = ({ mention }) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    let message =
        (mention?.comment?.data?.parent?.message ?? mention?.comment?.data?.message) || '';

    if (mention?.mentioned_in === CAPTION) {
        const username = activeSocialProfile?.slug;
        message = mention?.media?.caption
            ?.replaceAll(new RegExp(`@${username}`, 'g'), username)
            ?.replaceAll(new RegExp(username, 'g'), `@${username}`);
    }

    const tags = getIGUsernameTags(message);
    const { formatTextWithComponent } = useTransformStringOnOffset();
    const { awsTranslation } = useAwsTranslate(formatTextWithComponent(message, tags));

    return (
        <>
            {mention?.mentioned_in === TAG ? (
                <TagMessage username={mention.media?.username} />
            ) : (
                <Comment.Message tags={tags} awsTranslation={awsTranslation} />
            )}
        </>
    );
};

Mention.Message = Message;

const TagMessage = ({ username }) => {
    return (
        <div className={'font-GMedium text-xs italic leading-5 transition-opacity'}>
            <Trans
                i18nKey="conversations:mentions_tracker.tagged_in_post"
                components={{
                    a: (
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        <a
                            href={getIGUsernameLink(username)}
                            className="font-GSemiBold not-italic hover:text-icoBlue"
                            target="_blank"
                            rel="noopener noreferrer"
                        />
                    )
                }}
                values={{ username }}
            />
        </div>
    );
};

Mention.TagMessage = TagMessage;

const Thread = ({ mention, onReply }) => {
    const replies = mention?.comment?.data?.reply || mention?.media?.data?.reply;

    if (!mention?.comment && !replies) {
        return null;
    }

    return (
        <div className="mt-2.5 space-y-3 border-l pl-2.5 dark:border-icoDarkMode-beton">
            {mention?.comment ? (
                <>
                    <Mention.Header comment={mention?.comment} onReply={onReply} />
                    <Mention.Message mention={mention} />
                </>
            ) : null}
            {replies &&
                [replies]?.flat()?.map((reply) => <Mention.Reply key={reply?.id} reply={reply} />)}
        </div>
    );
};

Mention.Thread = Thread;

/**
 * Reply
 */

const Reply = ({ reply }) => {
    const { formatTextWithComponent } = useTransformStringOnOffset();
    const tags = getIGUsernameTags(reply?.message);

    return (
        <>
            <Mention.SubHeaderBasic username={reply?.username} />
            <Comment.Message message={formatTextWithComponent(reply?.message, tags)} tags={tags} />
        </>
    );
};

Mention.Reply = Reply;

export default Mention;
